// frontend/src/components/dashboard/ProductsManagement.js
import React, { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Typography,
    Menu,
    MenuItem,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Tooltip
} from '@mui/material';
import {
    Edit as EditIcon,
    Delete as DeleteIcon,
    GetApp as DownloadIcon
} from '@mui/icons-material';
import ProductDialog from './ProductDialog';
import ProductsImport from './ProductsImport';
import { useSettings } from '../../hooks/useSettings';
import { useTranslation } from 'react-i18next';
import axiosInstance from '../../utils/axios';

export default function ProductsManagement() {
    const { t } = useTranslation();
    const { formatPrice } = useSettings();
    const [products, setProducts] = useState([]);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [openDialog, setOpenDialog] = useState(false);
    const [loading, setLoading] = useState(false);
    const [exportAnchorEl, setExportAnchorEl] = useState(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [productToDelete, setProductToDelete] = useState(null);
    const [importDialogOpen, setImportDialogOpen] = useState(false);
    const [currentUser, setCurrentUser] = useState(null);

    useEffect(() => {
        fetchCurrentUser();
        fetchProducts();
    }, []);

    const fetchCurrentUser = async () => {
        try {
            const response = await axiosInstance.get('/auth/profile');
            setCurrentUser(response.data);
        } catch (error) {
            console.error('Error fetching user:', error);
        }
    };

    const fetchProducts = async () => {
        try {
            const response = await axiosInstance.get('/products');
            setProducts(response.data);
        } catch (error) {
            console.error('Error fetching products:', error);
        }
    };

    const handleExportClick = (event) => {
        setExportAnchorEl(event.currentTarget);
    };

    const handleExportClose = () => {
        setExportAnchorEl(null);
    };

    const handleExport = async (format) => {
        try {
            const response = await axiosInstance.get(`/products/export/${format}`, {
                responseType: 'blob'
            });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `products.${format}`);
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (error) {
            console.error('Error exporting products:', error);
        }
        handleExportClose();
    };

    const handleDeleteClick = (product) => {
        setProductToDelete(product);
        setDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = async () => {
        try {
            await axiosInstance.delete(`/products/${productToDelete._id}`);
            fetchProducts();
        } catch (error) {
            console.error('Error deleting product:', error);
        } finally {
            setDeleteDialogOpen(false);
            setProductToDelete(null);
        }
    };

    const handleDeleteCancel = () => {
        setDeleteDialogOpen(false);
        setProductToDelete(null);
    };

    const isAdminOrSuperAdmin = currentUser?.role === 'admin' || currentUser?.role === 'superadmin';

    return (
        <Box>
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6">{t('products.title')}</Typography>
                <Box>
                    <Button
                        variant="contained"
                        onClick={() => {
                            setSelectedProduct(null);
                            setOpenDialog(true);
                        }}
                        sx={{ mr: 1 }}
                    >
                        {t('products.addNew')}
                    </Button>
                    <Button
                        variant="outlined"
                        onClick={() => setImportDialogOpen(true)}
                        sx={{ mr: 1 }}
                        disabled={!isAdminOrSuperAdmin}
                    >
                        {t('common.import')}
                    </Button>
                    <Button
                        variant="outlined"
                        onClick={handleExportClick}
                        startIcon={<DownloadIcon />}
                    >
                        {t('common.export')}
                    </Button>
                </Box>
            </Box>

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>{t('products.name')}</TableCell>
                            <TableCell>{t('products.category')}</TableCell>
                            <TableCell>{t('products.price')}</TableCell>
                            <TableCell>{t('products.costPrice')}</TableCell>
                            <TableCell>{t('products.warehouse')}</TableCell>
                            <TableCell>{t('products.articleNumber')}</TableCell>
                            <TableCell>{t('products.unit')}</TableCell>
                            <TableCell>{t('products.dimensions')}</TableCell>
                            <TableCell>{t('products.stock')}</TableCell>
                            <TableCell>{t('products.responsibleEmployee')}</TableCell>
                            <TableCell>{t('common.actions')}</TableCell>
                        </TableRow>
                    </TableHead>

<TableBody>
    {Array.isArray(products) && products.length > 0 ? (
        products.map((product) => (
            <TableRow key={product?._id || 'default-key'}>
                <TableCell>{product?.name}</TableCell>
                <TableCell>{product?.category || ''}</TableCell>
                <TableCell>{product?.price ? formatPrice(product.price) : ''}</TableCell>
                <TableCell>{product?.costPrice ? formatPrice(product.costPrice) : ''}</TableCell>
                <TableCell>
                    {(product?.warehouse?.name || product?.warehouse?.address) ? (
                        <>
                            {product.warehouse.name}
                            <Typography variant="caption" display="block" color="textSecondary">
                                {product.warehouse.address}
                            </Typography>
                        </>
                    ) : ''}
                </TableCell>
                <TableCell>{product?.articleNumber || ''}</TableCell>
                <TableCell>{product?.unit ? t(`products.units.${product.unit}`) : ''}</TableCell>
                <TableCell>
                    {(product?.dimensions?.weight || 
                     product?.dimensions?.length || 
                     product?.dimensions?.width || 
                     product?.dimensions?.height) ? (
                        `${product.dimensions?.weight || '0'}kg, ${product.dimensions?.length || '0'}x${product.dimensions?.width || '0'}x${product.dimensions?.height || '0'}cm`
                    ) : ''}
                </TableCell>
                <TableCell>{product?.stock || ''}</TableCell>
                <TableCell>{product?.responsibleEmployee?.name || ''}</TableCell>
                <TableCell>
                    <IconButton
                        size="small"
                        onClick={() => {
                            setSelectedProduct(product);
                            setOpenDialog(true);
                        }}
                    >
                        <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDeleteClick(product)}
                    >
                        <DeleteIcon fontSize="small" />
                    </IconButton>
                </TableCell>
            </TableRow>
        ))
    ) : (
        <TableRow>
            <TableCell colSpan={11} align="center">
                <Typography>{t('products.noProducts')}</Typography>
            </TableCell>
        </TableRow>
    )}
</TableBody>

                </Table>
            </TableContainer>

            <Menu
                anchorEl={exportAnchorEl}
                open={Boolean(exportAnchorEl)}
                onClose={handleExportClose}
            >
                <MenuItem onClick={() => handleExport('xlsx')}>Excel (XLSX)</MenuItem>
                <MenuItem onClick={() => handleExport('xml')}>XML</MenuItem>
                <MenuItem onClick={() => handleExport('txt')}>Text (TXT)</MenuItem>
            </Menu>

            <ProductDialog
                open={openDialog}
                onClose={() => setOpenDialog(false)}
                product={selectedProduct}
                onSave={fetchProducts}
            />

            <Dialog
                open={deleteDialogOpen}
                onClose={handleDeleteCancel}
            >
                <DialogTitle>{t('products.deleteTitle')}</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {t('products.deleteConfirm', { name: productToDelete?.name })}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDeleteCancel}>{t('common.cancel')}</Button>
                    <Button onClick={handleDeleteConfirm} color="error" variant="contained">
                        {t('common.delete')}
                    </Button>
                </DialogActions>
            </Dialog>

            <ProductsImport 
                open={importDialogOpen}
                onClose={() => setImportDialogOpen(false)}
                onImportComplete={fetchProducts}
            />
        </Box>
    );
}